/**
 * Communications Dashboard API Contracts
 *
 * Pins the request/response shapes for the five Communications Dashboard
 * endpoints. The dashboard correlates published content (DB-backed) with
 * PostHog engagement and GetStream reactions, so several response maps are
 * keyed by `activityId` (= ContentRecord publicId) and tolerate missing /
 * degraded entries.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-05-28-003-feat-communications-dashboard-plan.md (U1).
 */

import { z } from 'zod'

/**
 * Reporting window. Only three fixed ranges are supported — there is no
 * custom date range in v1.
 */
export const CommsDashboardRangeSchema = z.enum(['7d', '30d', '90d'])

export type CommsDashboardRange = z.infer<typeof CommsDashboardRangeSchema>

/**
 * Content types selectable as a dashboard filter.
 *
 * NOTE: `HOME_CARD` is a `presentationStyle` on ContentRecord, not a member
 * of the backend `ContentType` enum — but the dashboard surfaces home cards
 * as a first-class filterable kind alongside the true content types. The
 * remaining five mirror `ContentType`. `EVENT`, `ACTION_ITEM`, and `POLL`
 * are intentionally excluded from the comms dashboard filter set.
 */
export const CommsDashboardContentTypeSchema = z.enum([
  'HOME_CARD',
  'ANNOUNCEMENT',
  'NEWS',
  'ALERT',
  'POST',
  'QUESTION'
])

export type CommsDashboardContentType = z.infer<typeof CommsDashboardContentTypeSchema>

/**
 * Upper bound on how many activities may be looked up in a single
 * engagement / reactions batch call. Keeps the PostHog HogQL `IN (...)` and
 * the Stream reaction fan-out bounded.
 */
export const COMMS_DASHBOARD_MAX_ACTIVITY_IDS = 20

const ActivityIdsSchema = z.array(z.string().min(1)).max(COMMS_DASHBOARD_MAX_ACTIVITY_IDS, {
  message: `activityIds may contain at most ${COMMS_DASHBOARD_MAX_ACTIVITY_IDS} ids per request`
})

// ============================================
// Posts
// GET /v1/system/communications/posts
// ============================================

/**
 * One row in the posts table. `audience` is the resolved recipient count for
 * the post (a single number, not a breakdown).
 */
export const CommsDashboardPostRowSchema = z.object({
  publicId: z.string(),
  contentType: CommsDashboardContentTypeSchema,
  presentationStyle: z.enum(['HOME_CARD']).nullable(),
  title: z.string(),
  authorName: z.string(),
  publishedAt: z.string(),
  audience: z.number()
})

export type CommsDashboardPostRow = z.infer<typeof CommsDashboardPostRowSchema>

export const CommsDashboardPostsRequestSchema = z.object({
  schoolId: z.string().min(1),
  range: CommsDashboardRangeSchema,
  contentTypes: z.array(CommsDashboardContentTypeSchema).optional(),
  authorId: z.string().min(1).optional(),
  cursor: z.string().min(1).optional()
})

export type CommsDashboardPostsRequest = z.infer<typeof CommsDashboardPostsRequestSchema>

export const CommsDashboardPostsResponseSchema = z.object({
  posts: z.array(CommsDashboardPostRowSchema),
  nextCursor: z.string().nullable(),
  lastUpdated: z.string()
})

export type CommsDashboardPostsResponse = z.infer<typeof CommsDashboardPostsResponseSchema>

// ============================================
// Community stats
// GET /v1/system/communications/community-stats
// ============================================

export const CommsDashboardCommunityStatsRequestSchema = z.object({
  schoolId: z.string().min(1)
})

export type CommsDashboardCommunityStatsRequest = z.infer<typeof CommsDashboardCommunityStatsRequestSchema>

export const CommsDashboardCommunityStatsResponseSchema = z.object({
  appUsers: z.number(),
  totalCommunity: z.number(),
  nonAppUsers: z.number(),
  lastUpdated: z.string()
})

export type CommsDashboardCommunityStatsResponse = z.infer<typeof CommsDashboardCommunityStatsResponseSchema>

// ============================================
// Engagement
// GET /v1/system/communications/engagement
// ============================================

/**
 * Engagement metrics for a single activity. Counts are PostHog-derived;
 * `*Unique` variants are distinct-person counts.
 */
export const CommsDashboardActivityEngagementSchema = z.object({
  seen: z.number(),
  seenUnique: z.number(),
  tapped: z.number(),
  tappedUnique: z.number(),
  pushOpened: z.number()
})

export type CommsDashboardActivityEngagement = z.infer<typeof CommsDashboardActivityEngagementSchema>

export const CommsDashboardEngagementRequestSchema = z.object({
  schoolId: z.string().min(1),
  activityIds: ActivityIdsSchema,
  range: CommsDashboardRangeSchema
})

export type CommsDashboardEngagementRequest = z.infer<typeof CommsDashboardEngagementRequestSchema>

/**
 * Engagement keyed by `activityId`. Missing entries are treated as all-zero
 * by the consumer (no row emitted when an activity had no events).
 */
export const CommsDashboardEngagementResponseSchema = z.object({
  engagement: z.record(z.string(), CommsDashboardActivityEngagementSchema)
})

export type CommsDashboardEngagementResponse = z.infer<typeof CommsDashboardEngagementResponseSchema>

// ============================================
// Reactions
// GET /v1/system/communications/reactions
// ============================================

/**
 * Stream reaction counts for a single activity. `null` (at the map-value
 * level) means the activity has no Stream feed presence (e.g. a HOME_CARD)
 * or the Stream lookup degraded (e.g. a 429).
 */
export const CommsDashboardActivityReactionsSchema = z.object({
  likes: z.number(),
  comments: z.number()
})

export type CommsDashboardActivityReactions = z.infer<typeof CommsDashboardActivityReactionsSchema>

export const CommsDashboardReactionsRequestSchema = z.object({
  schoolId: z.string().min(1),
  activityIds: ActivityIdsSchema
})

export type CommsDashboardReactionsRequest = z.infer<typeof CommsDashboardReactionsRequestSchema>

/**
 * Reactions keyed by `activityId`. A `null` value = HOME_CARD (no Stream
 * activity) or a Stream-429 degrade for that activity.
 */
export const CommsDashboardReactionsResponseSchema = z.object({
  reactions: z.record(z.string(), CommsDashboardActivityReactionsSchema.nullable())
})

export type CommsDashboardReactionsResponse = z.infer<typeof CommsDashboardReactionsResponseSchema>

// ============================================
// Timeline
// GET /v1/system/communications/timeline
// ============================================

export const CommsDashboardTimelineBucketSchema = z.object({
  bucketStart: z.string(),
  taps: z.number()
})

export type CommsDashboardTimelineBucket = z.infer<typeof CommsDashboardTimelineBucketSchema>

export const CommsDashboardTimelineRequestSchema = z.object({
  schoolId: z.string().min(1),
  contentPublicId: z.string().min(1),
  range: CommsDashboardRangeSchema
})

export type CommsDashboardTimelineRequest = z.infer<typeof CommsDashboardTimelineRequestSchema>

export const CommsDashboardTimelineResponseSchema = z.object({
  buckets: z.array(CommsDashboardTimelineBucketSchema)
})

export type CommsDashboardTimelineResponse = z.infer<typeof CommsDashboardTimelineResponseSchema>
